import { TagOutlined } from "@ant-design/icons";
import { Button, Card, Col, Collapse, DatePicker, Form, Input, InputNumber, Radio, Row, Select, Spin } from "antd";
import dayjs from "dayjs";
import { forwardRef, useCallback, useEffect, useImperativeHandle, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router";
import { onGetPackingReportTemplateVariableData } from "redux/actions";
import { RESET_PACKING_REPORT_TEMPLATE_VARIABLE_DATA } from "redux/ActionTypes";
import { toReadableText } from "utils/functions";
import { alertPop } from "../../../../apiServices/common/helper";
import { getPresetRanges } from "common/SearchForm/DateRangePresets";

const { Option } = Select;
const { TextArea } = Input;
const { RangePicker } = DatePicker;
const { Panel } = Collapse;

const AddLabelForm = forwardRef((props, ref) => {
  const {
    view,
    formId,
    id,
    edit,
    onDelete,
    index,
    initialData,
    selectedVariableLabel,
    onUpdateVariableLabel,
    onVariableTypeChange,
    onRegisterForm,
    labelForms,
    tableForms,
    activeKeys,
    onAccordionChange,
  } = props;

  const [panelTitle, setPanelTitle] = useState(`Label ${index + 1}`);
  const [selectedVariableType, setSelectedVariableType] = useState(null);
  const [slug, setSlug] = useState(null);
  const [fontColor, setFontColor] = useState("#000000");
  const [isFormReady, setIsFormReady] = useState(false);

  const [form] = Form.useForm();

  const dispatch = useDispatch();

  const variables = useSelector((state) => state.packingReportTemplate.variables);
  const fontWeights = useSelector((state) => state.packingReportTemplate.font_weights);
  const fontSizes = useSelector((state) => state.packingReportTemplate.font_sizes);
  const isApiDataLoading = useSelector((state) => state.packingReportTemplate.loading);
  const variableApiData = useSelector((state) => state.packingReportTemplate.variableData);
  const textAlignments = useSelector((state) => state.packingReportTemplate.text_alignments);

  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  // ux2(final-2): recompute "today" when the picker opens, not just at render (whole-branch
  // review M-2) — a tab left open across midnight otherwise offers a stale "Today"/"Yesterday".
  const [presetRanges, setPresetRanges] = useState(getPresetRanges);

  useEffect(() => {
    if (onRegisterForm && formId && form) {
      onRegisterForm(formId, form);
      setIsFormReady(true);
    }
  }, [onRegisterForm, formId, form]);

  useEffect(() => {
    if (initialData && form && isFormReady) {
      form.setFieldsValue({
        name: initialData.name,
        from_row: initialData.from_row ? parseInt(initialData.from_row) : null,
        to_row: initialData.to_row ? parseInt(initialData.to_row) : null,
        from_column: initialData.from_column ? parseInt(initialData.from_column) : null,
        to_column: initialData.to_column ? parseInt(initialData.to_column) : null,
        variable_slug: initialData.variable_slug || undefined,
        variable_reference: Number(initialData.variable_reference) || undefined,
        font_weight: initialData.font_weight || "normal",
        font_size: initialData.font_size || "12px",
        font_color: initialData.font_color || "#000000",
        text_alignment: initialData.text_alignment || "left",
        is_underlined: initialData.is_underlined !== undefined ? initialData.is_underlined : 0,
      });

      if (initialData.font_color) {
        setFontColor(initialData.font_color);
      }

      if (initialData.variable_slug) {
        handleVariableChange(initialData.variable_slug, {
          prefillValue: initialData.variable_value,
        });
      }

      if (initialData.name) {
        setPanelTitle(initialData.name);
      }
    }
  }, [initialData, form, isFormReady]);

  useImperativeHandle(ref, () => ({
    submit() {
      form
        .validateFields()
        .then(async (values) => {
          const bodyData = {
            ...values,
            variable_value: selectedVariableLabel,
          };
        })
        .catch(() => {
          alertPop("error", "Please check the form fields and try again.");
        });
    },
    discart() {
      form.resetFields();
      onUpdateVariableLabel(null);
    },
  }));

  const handleVariableChange = useCallback(
    async (val, options = {}) => {
      const { prefillValue } = options;

      if (!val) {
        onClearVariable();
        return;
      }

      if (form.getFieldValue("variable_slug") !== val) {
        form.setFieldsValue({ variable_reference: undefined });
        onUpdateVariableLabel(null);
      }

      setSlug(val);

      const selectedItem = variables.find((variable) => variable.slug === val);
      const nextType = selectedItem?.type || null;
      setSelectedVariableType(nextType);
      if (onVariableTypeChange) {
        onVariableTypeChange(formId, nextType);
      }

      if (nextType === "dropdown") {
        dispatch(onGetPackingReportTemplateVariableData({ slug: val }));
      } else {
        dispatch({ type: RESET_PACKING_REPORT_TEMPLATE_VARIABLE_DATA });
      }

      if (prefillValue != null) {
        if (nextType === "date") {
          const dateValue = dayjs(prefillValue);
          if (dateValue.isValid()) {
            form.setFieldsValue({ variable_reference: dateValue });
            onUpdateVariableLabel(prefillValue);
          }
        } else if (nextType === "text") {
          form.setFieldsValue({ variable_reference: prefillValue });
          onUpdateVariableLabel(prefillValue);
        } else if (nextType === "range") {
          const [start, end] = String(prefillValue).split(" - ");
          if (start && end) {
            const startDate = dayjs(start);
            const endDate = dayjs(end);
            if (startDate.isValid() && endDate.isValid()) {
              form.setFieldsValue({ variable_reference: [startDate, endDate] });
              onUpdateVariableLabel(prefillValue);
            }
          }
        }
      }
    },
    [dispatch, variables, form, formId, onVariableTypeChange, onUpdateVariableLabel],
  );

  useEffect(() => {
    const initial = form?.getFieldValue?.("font_color") || "#000000";
    if (initial) setFontColor(initial);
  }, [form]);

  const handleVariableReferenceChange = (value, option) => {
    if (selectedVariableType === "dropdown" && option) {
      onUpdateVariableLabel(option.children);
    } else {
      onUpdateVariableLabel(value);
    }
  };

  const onClearVariable = () => {
    setSelectedVariableType(null);
    setSlug(null);
    onUpdateVariableLabel(null);
    if (onVariableTypeChange) {
      onVariableTypeChange(formId, null);
    }
    dispatch({ type: RESET_PACKING_REPORT_TEMPLATE_VARIABLE_DATA });
    form.setFieldsValue({ variable_reference: undefined });
  };

  useEffect(() => {
    if (!slug) return;
    const selectedItem = variables.find((variable) => variable.slug === slug);
    const nextType = selectedItem?.type || null;
    if (selectedVariableType !== nextType) {
      setSelectedVariableType(nextType);
      if (onVariableTypeChange) {
        onVariableTypeChange(formId, nextType);
      }
    }
  }, [slug, variables, selectedVariableType, onVariableTypeChange, formId]);

  const renderVariableInput = () => {
    if (!selectedVariableType) return null;

    const formItemProps = {
      label: `${toReadableText(slug)}`,
      name: "variable_reference",
      rules: [
        {
          required: true,
          message: `${toReadableText(slug)} is required`,
        },
      ],
    };

    switch (selectedVariableType) {
      case "dropdown":
        return (
          <Col className="gutter-row" span={4}>
            <Form.Item {...formItemProps}>
              <Select
                placeholder="Select value"
                showSearch
                style={{ width: "100%" }}
                filterOption={(input, option) => option.children.toLowerCase().includes(input.toLowerCase())}
                disabled={view}
                allowClear
                size="small"
                onChange={handleVariableReferenceChange}
                onClear={() => onUpdateVariableLabel(null)}
              >
                {variableApiData?.map((item) => (
                  <Option key={item.id} value={item.id}>
                    {item.name}
                  </Option>
                ))}
              </Select>
            </Form.Item>
          </Col>
        );
      case "text":
        return (
          <Col className="gutter-row" span={4}>
            <Form.Item {...formItemProps}>
              <Input disabled={view} size="small" onChange={(e) => onUpdateVariableLabel(e.target.value)} />
            </Form.Item>
          </Col>
        );
      case "date":
        return (
          <Col className="gutter-row" span={4}>
            <Form.Item {...formItemProps}>
              <DatePicker
                style={{ width: "100%" }}
                disabled={view}
                size="small"
                onChange={(date, dateString) => {
                  if (date) {
                    onUpdateVariableLabel(dateString);
                  } else {
                    onUpdateVariableLabel(null);
                  }
                }}
              />
            </Form.Item>
          </Col>
        );
      case "range":
        return (
          <Col className="gutter-row" span={4}>
            <Form.Item {...formItemProps}>
              <RangePicker
                style={{ width: "100%" }}
                disabled={view}
                size="small"
                ranges={presetRanges}
                onOpenChange={(open) => open && setPresetRanges(getPresetRanges())}
                onChange={(dates, dateStrings) => {
                  if (dates && dates.length === 2) {
                    onUpdateVariableLabel(dateStrings.join(" - "));
                  } else {
                    onUpdateVariableLabel(null);
                  }
                }}
              />
            </Form.Item>
          </Col>
        );
      default:
        return null;
    }
  };

  const handleFromChange = (value) => {
    if (form) {
      form.setFieldsValue({ to_row: value });
    }
  };

  const handleFromColumnChange = (value) => {
    if (form) {
      form.setFieldsValue({ to_column: value });
    }
  };

  const getTableRowSpan = useCallback((hasHeaderValue, hasSubHeaderValue) => {
    let span = 1;
    if (hasHeaderValue) {
      span += 1;
    }
    if (hasSubHeaderValue) {
      span += 1;
    }
    return span;
  }, []);

  const extractExistingRanges = useCallback(() => {
    const ranges = [];

    if (labelForms && Array.isArray(labelForms)) {
      labelForms.forEach((labelForm) => {
        if (!labelForm || !labelForm.initialData) return;
        if (labelForm.id === formId) return;

        const fromRowValue =
          labelForm.initialData.from_row != null && !Number.isNaN(Number(labelForm.initialData.from_row))
            ? Number(labelForm.initialData.from_row)
            : null;
        if (fromRowValue == null) return;

        const toRowValue =
          labelForm.initialData.to_row != null && !Number.isNaN(Number(labelForm.initialData.to_row))
            ? Number(labelForm.initialData.to_row)
            : fromRowValue;

        ranges.push({
          type: "label",
          fromRow: fromRowValue,
          toRow: toRowValue,
        });
      });
    }

    if (tableForms && Array.isArray(tableForms)) {
      tableForms.forEach((tableForm) => {
        if (!tableForm || !tableForm.initialData) return;

        const fromRowValue =
          tableForm.initialData.from_row != null && !Number.isNaN(Number(tableForm.initialData.from_row))
            ? Number(tableForm.initialData.from_row)
            : null;
        if (fromRowValue == null) return;

        const hasHeaderValue =
          tableForm.initialData.has_table_header === 1 || tableForm.initialData.has_table_header === true;
        const hasSubHeaderValue =
          Array.isArray(tableForm.initialData.columns) &&
          tableForm.initialData.columns.some(
            (column) => column?.is_split && Array.isArray(column.split_columns) && column.split_columns.length > 0,
          );

        const span = getTableRowSpan(hasHeaderValue, hasSubHeaderValue);
        const toRowValue = fromRowValue + span - 1;

        ranges.push({
          type: "table",
          fromRow: fromRowValue,
          toRow: toRowValue,
          hasHeader: hasHeaderValue,
          hasSubHeader: hasSubHeaderValue,
        });
      });
    }

    return ranges.sort((a, b) => {
      if (a.toRow === b.toRow) {
        return a.fromRow - b.fromRow;
      }
      return a.toRow - b.toRow;
    });
  }, [formId, getTableRowSpan, labelForms, tableForms]);

  const resolveLabelPlacement = useCallback(() => {
    if (!form || !isFormReady) return;

    const existingRanges = extractExistingRanges();
    if (existingRanges.length === 0 && form.getFieldValue("from_row") == null) {
      const baseRow = 1;
      const currentToRow = form.getFieldValue("to_row");
      form.setFieldsValue({ from_row: baseRow, to_row: currentToRow != null ? currentToRow : baseRow });
      return;
    }

    const values = form.getFieldsValue(["from_row", "to_row"]);
    let proposedFrom =
      values?.from_row != null && !Number.isNaN(Number(values.from_row)) ? Number(values.from_row) : null;
    let proposedTo = values?.to_row != null && !Number.isNaN(Number(values.to_row)) ? Number(values.to_row) : null;

    if (proposedFrom != null && proposedTo != null && proposedTo < proposedFrom) {
      proposedTo = proposedFrom;
    }

    const height =
      proposedFrom != null && proposedTo != null && proposedTo >= proposedFrom ? proposedTo - proposedFrom + 1 : 1;

    const determineNextFromRow = () => {
      if (existingRanges.length === 0) {
        return 1;
      }

      const lastRange = existingRanges[existingRanges.length - 1];

      if (lastRange.type === "table") {
        return lastRange.toRow + 2;
      }

      return lastRange.toRow + 1;
    };

    if (proposedFrom == null) {
      proposedFrom = determineNextFromRow();
    }

    if (proposedTo == null || proposedTo < proposedFrom) {
      proposedTo = proposedFrom + height - 1;
    }

    const adjustForOverlap = () => {
      let candidateFrom = proposedFrom;
      let candidateTo = candidateFrom + (height > 0 ? height - 1 : 0);
      const sortedRanges = existingRanges.slice().sort((a, b) => a.fromRow - b.fromRow);

      const overlaps = (range) => !(candidateTo < range.fromRow || candidateFrom > range.toRow);

      const calculateShift = (range) => {
        if (range.type === "label") {
          return range.toRow + 1;
        }

        return range.toRow + 2;
      };

      let conflict = sortedRanges.find(overlaps);

      while (conflict) {
        candidateFrom = calculateShift(conflict);
        candidateTo = candidateFrom + (height > 0 ? height - 1 : 0);
        conflict = sortedRanges.find(overlaps);
      }

      return { from: candidateFrom, to: candidateTo };
    };

    const resolvedRange = adjustForOverlap();

    if (resolvedRange.from !== proposedFrom || resolvedRange.to !== proposedTo) {
      form.setFieldsValue({
        from_row: resolvedRange.from,
        to_row: resolvedRange.to,
      });
    }
  }, [extractExistingRanges, form, isFormReady]);

  useEffect(() => {
    resolveLabelPlacement();
  }, [resolveLabelPlacement]);

  const validateRangeAndOverlap = (_, value) => {
    if (!form) return Promise.resolve();

    const fromValue = form.getFieldValue("from_row");
    if (fromValue != null && value != null && value < fromValue) {
      return Promise.reject(new Error("To Row must be greater than or equal to From Row"));
    }

    const currentFrom = fromValue;
    const currentTo = value;

    if (labelForms && Array.isArray(labelForms)) {
      for (let i = 0; i < labelForms.length; i++) {
        if (i === index) continue;
        const otherFormData = labelForms[i];
        let otherFrom, otherTo;
        if (otherFormData?.initialData) {
          otherFrom = Number(otherFormData.initialData.from_row);
          otherTo = Number(otherFormData.initialData.to_row);
        } else if (otherFormData?.from_row && otherFormData?.to_row) {
          otherFrom = Number(otherFormData.from_row);
          otherTo = Number(otherFormData.to_row);
        }
        if (
          otherFrom != null &&
          otherTo != null &&
          currentFrom != null &&
          currentTo != null &&
          ((currentFrom >= otherFrom && currentFrom <= otherTo) ||
            (currentTo >= otherFrom && currentTo <= otherTo) ||
            (otherFrom >= currentFrom && otherFrom <= currentTo) ||
            (otherTo >= currentFrom && otherTo <= currentTo))
        ) {
          return Promise.reject(new Error("Row range overlaps with another label"));
        }
      }
    }

    if (tableForms && Array.isArray(tableForms) && currentFrom != null && currentTo != null) {
      for (let i = 0; i < tableForms.length; i++) {
        const tableData = tableForms[i];
        let tableFromRow, tableHasHeader;

        if (tableData?.initialData?.from_row) {
          tableFromRow = Number(tableData.initialData.from_row);
          tableHasHeader = tableData.initialData.has_table_header;
        }

        if (tableFromRow != null) {
          const tableRows = [tableFromRow];

          if (tableHasHeader === 1) {
            tableRows.push(tableFromRow + 1);
          }

          for (const tableRow of tableRows) {
            if (tableRow >= currentFrom && tableRow <= currentTo) {
              return Promise.reject(
                new Error(
                  `Row range overlaps with a table${tableHasHeader === 1 ? " (with header)" : ""} at row ${tableRow}`,
                ),
              );
            }
          }
        }
      }
    }

    return Promise.resolve();
  };

  return (
    <Collapse activeKey={activeKeys} onChange={onAccordionChange} className="mb-2">
      <Panel
        header={
          <span>
            <TagOutlined style={{ marginRight: 8, color: "#1890ff" }} />
            {panelTitle}
          </span>
        }
        key={`label-${index}`}
        extra={
          !view && (
            <Button
              danger
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                onDelete(index);
              }}
            >
              Delete
            </Button>
          )
        }
      >
        <div className="containt-body">
          <Form
            layout="vertical"
            form={form}
            name={`label-form-${index}`}
            initialValues={{
              font_weight: "normal",
              font_color: "#000000",
              font_size: "12px",
              text_alignment: "left",
              is_underlined: 0,
            }}
            onValuesChange={(changedValues) => {
              if (changedValues.name) {
                setPanelTitle(changedValues.name || `Label ${index + 1}`);
              }
            }}
          >
            <Card>
              {isApiDataLoading ? (
                <div style={{ display: "flex", justifyContent: "center", alignItems: "center", minHeight: "200px" }}>
                  <Spin spinning={isApiDataLoading} />
                </div>
              ) : (
                <Row gutter={6}>
                  <Col className="gutter-row" span={24}>
                    <Form.Item
                      label="Label Name"
                      name="name"
                      rules={[
                        {
                          required: true,
                          message: "Label Name is required",
                        },
                      ]}
                    >
                      <TextArea className="w-100" disabled={view} size="small" style={{ resize: "none" }} />
                    </Form.Item>
                  </Col>

                  <Col className="gutter-row" span={4}>
                    <Form.Item
                      label="From Row"
                      name="from_row"
                      rules={[
                        {
                          required: true,
                          message: "From Row is required",
                        },
                        {
                          validator: (_, value) => {
                            if (!form || value == null) return Promise.resolve();

                            if (labelForms && Array.isArray(labelForms)) {
                              for (let i = 0; i < labelForms.length; i++) {
                                if (i === index) continue;
                                const otherFormData = labelForms[i];
                                let otherFrom, otherTo;
                                if (otherFormData?.initialData) {
                                  otherFrom = Number(otherFormData.initialData.from_row);
                                  otherTo = Number(otherFormData.initialData.to_row);
                                }

                                if (otherFrom != null && otherTo != null) {
                                  if (value >= otherFrom && value <= otherTo) {
                                    return Promise.reject(
                                      new Error(`Row overlaps with another label (rows ${otherFrom}-${otherTo})`),
                                    );
                                  }
                                }
                              }
                            }

                            if (tableForms && Array.isArray(tableForms)) {
                              for (let i = 0; i < tableForms.length; i++) {
                                const tableData = tableForms[i];
                                let tableFromRow, tableHasHeader;

                                if (tableData?.initialData?.from_row) {
                                  tableFromRow = Number(tableData.initialData.from_row);
                                  tableHasHeader = tableData.initialData.has_table_header;
                                }

                                if (tableFromRow != null) {
                                  const tableRows = [tableFromRow];

                                  if (tableHasHeader === 1) {
                                    tableRows.push(tableFromRow + 1);
                                  }

                                  if (tableRows.includes(value)) {
                                    return Promise.reject(
                                      new Error(
                                        `Row ${value} overlaps with a table${
                                          tableHasHeader === 1 ? " (with header)" : ""
                                        }`,
                                      ),
                                    );
                                  }
                                }
                              }
                            }

                            return Promise.resolve();
                          },
                        },
                      ]}
                    >
                      <InputNumber
                        className="w-100"
                        disabled={view}
                        size="small"
                        style={{ width: "100%" }}
                        onChange={handleFromChange}
                      />
                    </Form.Item>
                  </Col>

                  <Col className="gutter-row" span={4}>
                    <Form.Item
                      label="To Row"
                      name="to_row"
                      rules={[
                        {
                          required: false,
                          message: "To Row is required",
                        },
                        {
                          validator: validateRangeAndOverlap,
                        },
                      ]}
                    >
                      <InputNumber className="w-100" disabled={view} size="small" style={{ width: "100%" }} />
                    </Form.Item>
                  </Col>

                  <Col className="gutter-row" span={4}>
                    <Form.Item
                      label="From Column"
                      name="from_column"
                      rules={[
                        {
                          required: true,
                          message: "From Column is required",
                        },
                      ]}
                    >
                      <InputNumber
                        className="w-100"
                        disabled={view}
                        size="small"
                        style={{ width: "100%" }}
                        onChange={handleFromColumnChange}
                      />
                    </Form.Item>
                  </Col>

                  <Col className="gutter-row" span={4}>
                    <Form.Item
                      label="To Column"
                      name="to_column"
                      rules={[
                        {
                          required: false,
                          message: "To Column is required",
                        },
                      ]}
                    >
                      <InputNumber className="w-100" disabled={view} size="small" style={{ width: "100%" }} />
                    </Form.Item>
                  </Col>

                  <Col className="gutter-row" span={4}>
                    <Form.Item
                      label="Set Value"
                      name="variable_slug"
                      rules={[
                        {
                          required: false,
                          message: "Variable Reference is required",
                        },
                      ]}
                    >
                      <Select
                        placeholder="Set Value"
                        showSearch
                        style={{ width: "100%" }}
                        onChange={handleVariableChange}
                        filterOption={(input, option) => option.children.toLowerCase().includes(input.toLowerCase())}
                        disabled={view}
                        allowClear
                        size="small"
                        onClear={onClearVariable}
                      >
                        {variables?.map((variable) => (
                          <Option key={variable.slug} value={variable.slug}>
                            {variable.name}
                          </Option>
                        ))}
                      </Select>
                    </Form.Item>
                  </Col>

                  {renderVariableInput()}

                  <Col className="gutter-row" span={4}>
                    <Form.Item
                      label="Font weight"
                      name="font_weight"
                      rules={[
                        {
                          required: true,
                          message: "Font weight is required",
                        },
                      ]}
                    >
                      <Select
                        placeholder="Font Size"
                        showSearch
                        style={{ width: "100%" }}
                        filterOption={(input, option) => option.children.toLowerCase().includes(input.toLowerCase())}
                        disabled={view}
                        allowClear
                        size="small"
                      >
                        {fontWeights?.map((fontWeight) => (
                          <Option key={fontWeight.value} value={fontWeight.value}>
                            {fontWeight.label}
                          </Option>
                        ))}
                      </Select>
                    </Form.Item>
                  </Col>

                  <Col className="gutter-row" span={4}>
                    <Form.Item
                      label="Font size"
                      name="font_size"
                      rules={[
                        {
                          required: false,
                          message: "Font Size is required",
                        },
                      ]}
                    >
                      <Select
                        placeholder="Font Size"
                        showSearch
                        style={{ width: "100%" }}
                        filterOption={(input, option) => option.children.toLowerCase().includes(input.toLowerCase())}
                        disabled={view}
                        allowClear
                        size="small"
                      >
                        {fontSizes?.map((fontSize) => (
                          <Option key={fontSize.value} value={fontSize.value}>
                            {fontSize.label}
                          </Option>
                        ))}
                      </Select>
                    </Form.Item>
                  </Col>

                  <Col className="gutter-row" span={4}>
                    <Form.Item
                      label="Font Color"
                      name="font_color"
                      rules={[
                        {
                          required: false,
                          message: "Font Color is required",
                        },
                      ]}
                    >
                      <Input
                        placeholder="#FFFFFF"
                        value={fontColor}
                        onChange={(e) => {
                          const v = e.target.value;
                          setFontColor(v);
                          form.setFieldsValue({ font_color: v });
                        }}
                        disabled={view}
                        size="small"
                        addonAfter={
                          <input
                            type="color"
                            value={fontColor}
                            onChange={(e) => {
                              const v = e.target.value;
                              setFontColor(v);
                              form.setFieldsValue({ font_color: v });
                            }}
                            disabled={view}
                            style={{
                              width: 36,
                              height: 28,
                              padding: 0,
                              border: "none",
                              background: "transparent",
                              cursor: view ? "not-allowed" : "pointer",
                            }}
                          />
                        }
                      />
                    </Form.Item>
                  </Col>

                  <Col className="gutter-row" span={4}>
                    <Form.Item
                      label="Text Alignment"
                      name="text_alignment"
                      rules={[
                        {
                          required: true,
                          message: "Text Alignment is required",
                        },
                      ]}
                    >
                      <Select
                        placeholder="Text Alignment"
                        showSearch
                        style={{ width: "100%" }}
                        filterOption={(input, option) => option.children.toLowerCase().includes(input.toLowerCase())}
                        disabled={view}
                        allowClear
                        size="small"
                      >
                        {textAlignments?.map((textAlignment) => (
                          <Option key={textAlignment.value} value={textAlignment.value}>
                            {textAlignment.label}
                          </Option>
                        ))}
                      </Select>
                    </Form.Item>
                  </Col>

                  <Col className="gutter-row" span={4}>
                    <Form.Item
                      label="Is Underline"
                      name="is_underlined"
                      rules={[
                        {
                          required: false,
                        },
                      ]}
                    >
                      <Radio.Group disabled={view} size="small" className="mt-1">
                        <Radio value={1}>Yes</Radio>
                        <Radio value={0}>No</Radio>
                      </Radio.Group>
                    </Form.Item>
                  </Col>
                </Row>
              )}
            </Card>
          </Form>
        </div>
      </Panel>
    </Collapse>
  );
});

export default AddLabelForm;
